import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, UserButton, SignOutButton } from "@clerk/react";
import { BarChart3, CalendarDays, FileText, Gift, Image, Inbox, Megaphone, Mic2, Quote, Settings, Ticket, Users, Handshake, ExternalLink } from "lucide-react";
import { useAdminApi } from "./useAdminApi";
import { errMsg } from "@/lib/api";
import AdminOverview from "./AdminOverview";
import AdminSettings from "./AdminSettings";
import AdminCollection, { COLLECTION_CONFIG } from "./AdminCollection";
import AdminMoney from "./AdminMoney";
import AdminSubmissions from "./AdminSubmissions";
import AdminMedia from "./AdminMedia";

const TABS = [
  { k: "overview", label: "Overview & reports", icon: BarChart3 },
  { k: "campaign", label: "Campaign & content", icon: Settings },
  { k: "donations", label: "Donations & donor wall", icon: Gift },
  { k: "sponsors", label: "Sponsors", icon: Handshake },
  { k: "tickets", label: "Ticket sales", icon: Ticket },
  { k: "events", label: "Events", icon: CalendarDays },
  { k: "ticket_tiers", label: "Ticket tiers", icon: Ticket },
  { k: "sponsorships", label: "Sponsor packages", icon: Megaphone },
  { k: "testimonials", label: "Testimonials", icon: Quote },
  { k: "poets", label: "Featured poets", icon: Mic2 },
  { k: "gallery", label: "Photo gallery", icon: Image },
  { k: "team", label: "Program team (Impact page)", icon: Users },
  { k: "submissions", label: "Forms & applications", icon: Inbox },
  { k: "media", label: "Media library", icon: FileText },
];

const Body = ({ tab }) => {
  if (tab === "overview") return <AdminOverview />;
  if (tab === "campaign") return <AdminSettings />;
  if (tab === "donations") return <AdminMoney kind="donation" />;
  if (tab === "sponsors") return <AdminMoney kind="sponsorship" />;
  if (tab === "tickets") return <AdminMoney kind="ticket" />;
  if (tab === "submissions") return <AdminSubmissions />;
  if (tab === "media") return <AdminMedia />;
  if (COLLECTION_CONFIG[tab]) return <AdminCollection key={tab} coll={tab} />;
  return null;
};

const Dashboard = () => {
  const api = useAdminApi();
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  useEffect(() => {
    api("get", "/me").then(setMe).catch((e) => setError(errMsg(e)));
  }, [api]);

  if (error) return <div className="min-h-screen bg-mas-cream text-ink p-10 text-center" data-testid="admin-error">{error}</div>;
  if (!me) return <div className="min-h-screen bg-mas-cream text-ink p-10 text-center font-mono text-sm" data-testid="admin-loading">Loading dashboard...</div>;
  if (me.role !== "admin")
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center bg-mas-cream text-ink" data-testid="admin-no-access">
        <h1 className="text-3xl font-black">No admin access</h1>
        <p className="text-mas-mute max-w-md">This account isn't an administrator. Ask an existing admin to grant you access.</p>
        <SignOutButton><button className="btn-ink" data-testid="admin-sign-out-button">Sign out</button></SignOutButton>
      </div>
    );
  const current = TABS.find((t) => t.k === tab);
  return (
    <div className="admin-scope min-h-screen bg-mas-cream text-ink lg:grid lg:grid-cols-[260px_1fr]" data-testid="admin-dashboard">
      <aside className="bg-mas-ink text-mas-cream lg:min-h-screen lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto">
        <div className="p-5 flex items-center justify-between border-b border-white/10">
          <a href="/" className="font-serif font-black text-xl">MAS<span className="text-mas-orange">.</span> <span className="font-mono text-[10px] uppercase tracking-widest text-mas-sand">admin</span></a>
          <UserButton />
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm whitespace-nowrap transition-colors ${tab === t.k ? "bg-mas-terra text-mas-cream" : "text-mas-sand hover:bg-white/10"}`}>
              <t.icon className="w-4 h-4 shrink-0" />{t.label}
            </button>
          ))}
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2.5 text-sm text-mas-sand hover:text-mas-orange" data-testid="admin-view-site-link"><ExternalLink className="w-4 h-4" />View site</a>
        </nav>
      </aside>
      <main className="p-5 sm:p-8 lg:p-10 min-w-0 text-ink">
        <h1 className="text-3xl sm:text-4xl font-black mb-8" data-testid="admin-page-title">{current?.label}</h1>
        <Body tab={tab} />
      </main>
    </div>
  );
};

export default function AdminApp() {
  return (
    <>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
